/**
 * Portal routes come in a view and an edit flavour that mirror each other
 * under an `/edit` prefix, so every link has to be built for the mode it is
 * navigating within. Build them here rather than inline so a link can never
 * drop the caller out of the mode they are in.
 */

/**
 * Whether a pathname is the given base or sits below it. Matching on the
 * segment rather than the raw string keeps `/portals/1` from claiming
 * `/portals/11`, and `/edit` from claiming `/editorial`.
 */
export const isWithinPath = (pathname: string, base: string) =>
  pathname === base || pathname.startsWith(`${base}/`);

export const portalBasePath = (portalId: string, isEditMode = false) =>
  isEditMode ? `/portals/${portalId}/edit` : `/portals/${portalId}`;

export const portalMediaPath = (
  portalId: string,
  mediaId?: string,
  isEditMode = false,
) => {
  const mediaBase = `${portalBasePath(portalId, isEditMode)}/media`;
  return mediaId ? `${mediaBase}/${mediaId}` : mediaBase;
};

/**
 * The view counterpart of an edit-mode pathname, keeping whichever sub-page the
 * caller is on so that leaving edit mode does not also move them off the asset
 * they were looking at. Falls back to the cover for any other pathname.
 */
export const portalViewPathFor = (portalId: string, editPathname: string) => {
  const editBase = portalBasePath(portalId, true);
  const viewBase = portalBasePath(portalId);
  return isWithinPath(editPathname, editBase)
    ? `${viewBase}${editPathname.slice(editBase.length)}`
    : viewBase;
};

/**
 * The edit counterpart of a view-mode pathname, so entering edit mode flips the
 * mode without moving the caller off the asset they were looking at. Falls back
 * to the cover for any other pathname.
 */
export const portalEditPathFor = (portalId: string, viewPathname: string) => {
  const viewBase = portalBasePath(portalId);
  const editBase = portalBasePath(portalId, true);
  if (!isWithinPath(viewPathname, viewBase)) {
    return editBase;
  }
  // Idempotent for a pathname already in the edit subtree, which the view
  // counterpart gets for free by having a base that ends in `/edit`: without
  // this the segment would be added twice, and `/edit/edit` is not a route.
  const base = isWithinPath(viewPathname, editBase) ? editBase : viewBase;
  return `${editBase}${viewPathname.slice(base.length)}`;
};
